import useSession from '@/hooks/useSession';
import useItem from '@/pages/Thread/hooks/useItem';
import useThread from '@/pages/Thread/hooks/useThread';
import styles from './source-indicator.module.scss';
import { FC, MouseEvent as ReactMouseEvent } from 'react';
import PDFIcon from '@/assets/icons/file-pdf.svg?react';
import NewsIcon from '@/assets/icons/newspaper.svg?react';
import TableIcon from '@/assets/icons/table.svg?react';
import { classnames } from '@/utils/functions';
import { getSourceType, SourceType } from '@/pages/Thread/utils/sources';
import { PENDING_CITATION_MESSAGE } from '@/pages/Thread/utils/inlineCitations';

interface SourceIndicatorProps {
  id: number;
  onClick?: () => void;
  /* An inline marker still streaming: an empty pale dot, not yet interactive */
  pending?: boolean;
  /* A marker inside the answer text: no chunk highlight on hover, tighter spacing */
  inline?: boolean;
}

const SOURCE_TYPES: Record<SourceType, { title: string; icon: JSX.Element }> = {
  table: { title: 'a Table', icon: <TableIcon /> },
  pdf: { title: 'a PDF', icon: <PDFIcon /> },
  article: { title: 'an Article', icon: <NewsIcon /> },
};

const SourceIndicator: FC<SourceIndicatorProps> = ({
  id,
  onClick = undefined,
  pending = false,
  inline = false,
}) => {
  const { pushUserEvent } = useSession();
  const { status } = useThread();
  const { item, isLast } = useItem();
  const sources = item.sources;
  const index = id - 1;
  const className = classnames(styles['source-indicator'], {
    [styles['source-indicator-inline']]: inline,
  });

  if (pending) {
    return (
      // Announced once per answer by Response, not once per marker
      <span
        className={classnames(className, styles['source-indicator-pending'])}
        data-state="pending"
        title={PENDING_CITATION_MESSAGE}
        aria-hidden="true"
      />
    );
  }

  if (!sources || sources?.length <= index) {
    return null;
  }

  const type = SOURCE_TYPES[getSourceType(sources[index])];

  const handleCitationWrapperGlow = (
    enable: boolean,
    elem: ReactMouseEvent<HTMLButtonElement>
  ) => {
    const parent = (elem.target as HTMLElement).closest('div');
    if (parent) {
      parent.style.color = enable ? 'var(--color-citation-glow)' : '';
    }
  };

  return typeof onClick === 'function' ? (
    <button
      className={className}
      aria-label={`Source ${id}: ${sources[index].title}`}
      onMouseEnter={
        !inline ? (elem) => handleCitationWrapperGlow(true, elem) : undefined
      }
      onMouseLeave={
        !inline ? (elem) => handleCitationWrapperGlow(false, elem) : undefined
      }
      onMouseUp={
        !inline ? (elem) => handleCitationWrapperGlow(false, elem) : undefined
      }
      onClick={() => {
        onClick();
        pushUserEvent('click_citation');
      }}
      disabled={isLast && status !== 'READY'}
    >
      {id}
    </button>
  ) : (
    <p className={styles['source-indicator-wrapper']}>
      <span className={className}>{id}</span>
      <i title={`Source is ${type.title}.`}>{type.icon}</i>
    </p>
  );
};

export default SourceIndicator;
